import React, { useState, useEffect, lazy, Suspense } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from './firebase';
import Sidebar from './components/Sidebar';
import NotificationBell from './components/NotificationBell';
import Login from './pages/Login';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Bookings = lazy(() => import('./pages/Bookings'));
const Barbers = lazy(() => import('./pages/Barbers'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Settings = lazy(() => import('./pages/Settings'));
const Clients = lazy(() => import('./pages/Clients'));
const Reports = lazy(() => import('./pages/Reports'));
const OnlineProfile = lazy(() => import('./pages/OnlineProfile'));
const Products = lazy(() => import('./pages/Products'));
const AuditLog = lazy(() => import('./pages/AuditLog'));
import config from './config';
import { collection, getDocs, orderBy, query, doc, getDoc } from 'firebase/firestore';
import './App.css';

async function loadServicesIntoConfig(tenantId) {
  if (!tenantId) return;
  try {
    const snap = await getDocs(query(collection(db, `tenants/${tenantId}/services`), orderBy('order', 'asc')));
    if (!snap.empty) {
      config.services = snap.docs
        .map((d) => {
          const data = d.data() || {};
          return { ...data, id: String(data.id || d.id || '').trim() };
        })
        .filter((s) => !!s.id);
    }
  } catch {}
}

function App() {
  const [authUser, setAuthUser] = useState(undefined); // undefined = still checking
  const [tenantId, setTenantId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(true); // default true until role loaded
  const [activePage, setActivePage] = useState('dashboard');
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [configReady, setConfigReady] = useState(false);

  useEffect(() => {
    document.body.classList.toggle('light', theme === 'light');
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Persist session across page refreshes via Firebase auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const tid = 'eekurt';
        setTenantId(tid);
        // Fetch role from Firestore — no doc = admin (backwards compat for owner)
        try {
          const staffDoc = await getDoc(doc(db, `tenants/${tid}/staff`, firebaseUser.uid));
          if (staffDoc.exists()) {
            setIsAdmin(staffDoc.data().role === 'owner'); // only owner sees delete/cancel
          } else {
            setIsAdmin(true); // no doc = owner (backwards compat)
          }
        } catch {
          setIsAdmin(true);
        }
        // Load services now that we know the tenant
        await loadServicesIntoConfig(tid);
        setConfigReady(true);
        setAuthUser(firebaseUser);
      } else {
        setAuthUser(null);
        setTenantId(null);
        setIsAdmin(true);
        setConfigReady(true);
      }
    });
    return unsubscribe;
  }, []);

  // Reload services when Services page saves changes
  useEffect(() => {
    const handler = () => loadServicesIntoConfig(tenantId);
    window.addEventListener('services-updated', handler);
    return () => window.removeEventListener('services-updated', handler);
  }, [tenantId]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');
  const handleLogout = () => auth.signOut();

  if (authUser === undefined || !configReady) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0a0a0a', color: '#c8c8c8', fontSize: '0.9rem', letterSpacing: '2px' }}>
        Loading...
      </div>
    );
  }

  if (!authUser || !tenantId) {
    return <Login onLogin={() => {}} />;
  }

  const renderPage = () => {
    switch (activePage) {
      case 'dashboard':     return <Dashboard tenantId={tenantId} isAdmin={isAdmin} />;
      case 'bookings':      return <Bookings tenantId={tenantId} isAdmin={isAdmin} />;
      case 'barbers':       return <Barbers tenantId={tenantId} isAdmin={isAdmin} />;
      case 'online-profile':return <OnlineProfile tenantId={tenantId} isAdmin={isAdmin} />;
      case 'calendar':      return <Calendar tenantId={tenantId} isAdmin={isAdmin} />;
      case 'clients':       return <Clients tenantId={tenantId} isAdmin={isAdmin} />;
      case 'reports':       return <Reports tenantId={tenantId} isAdmin={isAdmin} />;
      case 'products':      return <Products tenantId={tenantId} isAdmin={isAdmin} />;
      case 'settings':      return <Settings theme={theme} onToggleTheme={toggleTheme} tenantId={tenantId} isAdmin={isAdmin} authUser={authUser} />;
      case 'activity-log':  return isAdmin ? <AuditLog tenantId={tenantId} /> : <Dashboard tenantId={tenantId} isAdmin={isAdmin} />;
      default:              return <Dashboard tenantId={tenantId} isAdmin={isAdmin} />;
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: theme === 'light' ? '#f5f5f0' : '#0a0a0a' }}>
      <Sidebar
        activePage={activePage}
        setActivePage={setActivePage}
        onLogout={handleLogout}
        theme={theme}
        onToggleTheme={toggleTheme}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        tenantId={tenantId}
        isOwner={isAdmin}
      />
      <div style={{ position: 'fixed', top: '16px', right: '24px', zIndex: 200 }}>
        <NotificationBell tenantId={tenantId} />
      </div>
      <main style={{
        flex: 1,
        marginLeft: isCollapsed ? '80px' : '200px',
        padding: '60px 32px 32px',
        overflowY: 'auto',
        transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        <Suspense fallback={<div style={{ color: '#888', padding: '40px', textAlign: 'center' }}>Loading...</div>}>
          {renderPage()}
        </Suspense>
      </main>
    </div>
  );
}

export default App;
